package de.oevbs.kunde.kundenportal2.components

import de.oevbs.kosmos.ui.feedback.renderMessages
import de.oevbs.kosmos.ui.forms.buttontoggle.ButtonToggle
import de.oevbs.kosmos.ui.foundation.BooleanFlowProperty
import de.oevbs.kosmos.ui.foundation.ifDebug
import de.oevbs.kosmos.ui.foundation.wiredTo
import dev.fritz2.core.*
import dev.fritz2.headless.components.checkboxGroup
import dev.fritz2.headless.components.radioGroup
import dev.fritz2.headless.foundation.DatabindingProperty
import dev.fritz2.headless.validation.ComponentValidationMessage
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLLabelElement

class ButtonToggleKupo<T>(id: String?) {

    /**
     * Erlaubt den Zugriff auf die ID der Komponente im Initialisierungsblock der Factory-Funktion.
     * Eine explizite Angabe durch den Benutzer ist über die Factory-Funktionen möglich. Die folgenden Prioritäten
     * gelten bei der Vergabe der ID (höchste zu niedrigste Priorität):
     *
     * Explizite ID → Store-ID des Data Bindings ([value][ButtonToggle.value]) → Zufällig generierte ID ([Id.next])
     */
    private val componentId: String by lazy { id ?: value.multi.id ?: value.single.id ?: Id.next() }


    /**
     * Diese [DatabindingProperty] koppelt externe Datenquellen als Two-Way-Databinding an die [ButtonToggle].
     * Dazu wird ein [Flow] von [T] als externe Quelle für den Wert des Eingabefeldes benötigt.
     * Zusätzlich kann eine ID angegeben oder übernommen werden, sowie ein [Flow] von [ComponentValidationMessage]s
     * und eine Funktion, die eine [Flow] von [T] nach Unit abbildet, in welcher dann ein passender Handler
     * implementiert werden kann.
     *
     * Beispiel(e):
     * ```
     * enum class NotificationMethod(val displayName: String) {
     *     Email("Email"),
     *     Phone("Phone"),
     *     PushNotification("Push-Benachrichtigung")
     * }
     * val storedValue = storeOf(NotificationMethod.Email)
     *
     * // 1. Two-way Databinding durch einen Store
     * value.single(storedValue)
     *
     * // 2. One-way Databinding durch einen Flow
     * value.single(storedValue.data)
     *
     * // 3. Two-way Databinding durch separate Parameter
     * value.single(
     *     id = storedValue.id,
     *     data = storedValue.data,
     *     messages = storedValue.messages,
     *     handler = {
     *         it handledBy {
     *             console.log("Gewählte Methode: ${it.name}")
     *             storedValue.update(it)
     *         }
     *      })
     *
     * // 4. Databinding mit Mehrfachauswahl
     * value.multi(storeOf(emptyList))
     * ```
     *
     * @see DatabindingProperty
     */
    val value: Value<T> = Value()

    data class Value<T>(
        /**
         * @see ButtonToggle.value
         */
        val single: DatabindingProperty<T> = DatabindingProperty(),
        /**
         * @see ButtonToggle.value
         */
        val multi: DatabindingProperty<List<T>> = DatabindingProperty()
    )


    /**
     * Deaktiviert das Eingabeelement, sodass keine Eingabe mehr möglich ist. Zudem ist das Element nicht mehr
     * sequenziell fokussierbar, wird also nicht fürs Tabbing (Navigation per Tab-Taste) berücksichtigt.
     *
     * Beispiel(e):
     * ```
     * // 1. Statisches Deaktivieren
     * disabled(true)
     *
     * // 2. Dynamisches Deaktivieren durch einen externen Flow
     * val storedEditMode = storeOf(false)
     * disabled(storedEditMode.data)
     * ```
     *
     * @see BooleanFlowProperty
     */
    val disabled: BooleanFlowProperty = BooleanFlowProperty()


    /**
     * Enthält alle Werte des Typs [T], aus denen über die [ButtonToggle] ausgewählt werden soll.
     * Die einzelnen Optionen werden zur Darstellung auf der Nutzeroberfläche standardmäßig über [T]`.toString()`
     * formatiert. Eine benutzerdefinierte Formatierung kann über [optionsFormat] gewählt werden.
     *
     * Beispiel(e):
     * ```
     * // 1. Liste von Optionen
     * data class Insurance(val id: Int, val name: String)
     * options = listOf(
     *     Insurance(1, "Haftpflicht"),
     *     Insurance(2, "Kfz"),
     *     Insurance(3, "Rechtsschutz")
     * )
     *
     * // 2. Optionen aus den Werten einer Enumeration
     * enum class NotificationMethod(val displayName: String) {
     *     Email("Email"),
     *     Phone("Phone"),
     *     PushNotification("Push-Benachrichtigung")
     * }
     * options = NotificationMethod.values().asList()
     * ```
     */
    var options: List<T> = emptyList()

    /**
     * Über diesen Lambda-Ausdruck erfolgt eine Formatierung der in [options] übergebenen Werte zu einer List<String>,
     * deren Inhalte untereinander auf dem Button ausgegeben werden.
     * Wird keine Formatierung explizit angegeben, erfolgt die Formatierung ohne Zeilenumbrüche über [T]`.toString()`.
     *
     * Beispiel(e):
     * ```
     * // 1. Formatierung als Attribut des Option-Typs
     * data class Insurance(val id: Int, val name: String)
     * optionsFormat = { (id, name) -> "($id) $name" }
     *
     * // 2. Formatierung von Enum-Werten
     * enum class NotificationMethod(val displayName: String) {
     *     Email("Email"),
     *     Phone("Phone"),
     *     PushNotification("Push-Benachrichtigung")
     * }
     * optionsFormat = NotificationMethod::displayName
     * ```
     */
    var optionsFormat: (T) -> List<String> = { listOf(it.toString()) }


    private object CommonStyles {

        const val OPTIONS_CONTAINER_GRID = "grid grid-cols-[repeat(auto-fit,_minmax(10rem,_1fr))] auto-rows-fr items-stretch gap-2"

        const val OPTION_INPUT = "peer absolute inset-0 opacity-0 w-full h-full cursor-pointer"

        const val OPTION_LABEL = "leading-none text-sm overflow-ellipsis overflow-hidden"
    }


    fun RenderContext.render(): Tag<HTMLDivElement> {
        if (value.single.isSet && value.multi.isSet) {
            console.error("you must not use single and multi simultaneously")
        }

        return if (value.single.isSet) {
            renderSingle()
        } else {
            renderMulti()
        }
    }

    private fun RenderContext.renderSingle(): Tag<HTMLDivElement> =
        radioGroup(
            id = componentId,
            classes = CommonStyles.OPTIONS_CONTAINER_GRID,
        ) {
            value wiredTo this@ButtonToggleKupo.value.single

            disabled(disabled.falseIfAbsent)
            className(disabled.falseIfAbsent.map { if (it) "pointer-events-none" else "" })

            options.forEachIndexed { index, option ->
                radioGroupOption(option, "relative", id = "option$index") {
                    radioGroupOptionToggle(
                        classes = CommonStyles.OPTION_INPUT,
                        tag = RenderContext::input
                    ) {
                        type("radio")
                        checked(selected)
                        ifDebug?.attr("data-active", active)
                    }
                    renderOption(optionsFormat(option), value.hasError) {
                        radioGroupOptionLabel(
                            classes = CommonStyles.OPTION_LABEL,
                            content = it
                        )
                    }
                }
            }

            radioGroupValidationMessages(tag = RenderContext::ul) {
                renderMessages(msgs)
            }
        }

    private fun RenderContext.renderMulti(): Tag<HTMLDivElement> =
        checkboxGroup(
            id = componentId,
            classes = CommonStyles.OPTIONS_CONTAINER_GRID,
        ) {
            value wiredTo this@ButtonToggleKupo.value.multi

            disabled(disabled.falseIfAbsent)
            className(disabled.falseIfAbsent.map { if (it) "pointer-events-none" else "" })

            options.forEachIndexed { index, option ->
                checkboxGroupOption(option, "relative", id = "option$index") {
                    checkboxGroupOptionToggle(
                        classes = CommonStyles.OPTION_INPUT,
                        tag = RenderContext::input
                    ) {
                        type("checkbox")
                        checked(selected)
                    }
                    renderOption(optionsFormat(option), value.hasError) {
                        checkboxGroupOptionLabel(
                            classes = CommonStyles.OPTION_LABEL,
                            content = it
                        )
                    }
                }
            }

            checkboxGroupValidationMessages(tag = RenderContext::ul) {
                renderMessages(msgs)
            }
        }

    /**
     * Rendert eine einzige Auswahloption
     *
     * @param optionTextList enthält die untereinander auszugebenden Labeltext-Liste der Option
     * @param labelWrapper Lambda-Ausdruck, um einen Wrapper für die Headless Funktionen um das Label legen zu können
     */
    private fun RenderContext.renderOption(
        optionTextList: List<String>,
        hasError: Flow<Boolean>,
        labelWrapper: RenderContext.(Tag<HTMLLabelElement>.() -> Unit) -> Unit
    ) {
        div(
            joinClasses(
                "h-full flex align-center text-center items-center justify-center rounded shadow peer-checked:shadow-inner",
                "border-2 text-opacity-100 px-2 py-1 ring-transparent",
            )
        ) {
            className(combine(disabled.falseIfAbsent, hasError, ::Pair).map { (disabled, error) ->
                if (disabled) joinClasses(
                    "text-gray-300 peer-checked:text-gray-400",
                    "peer-checked:border-gray-400",
                    "pointer-events-none"
                )
                else joinClasses(
                    "text-secondary-700 peer-checked:text-gray-900",
                    if (error) "border-error-700 peer-checked:border-error-700"
                    else joinClasses(
                        "peer-checked:border-primary-700",
                        "peer-hover:border-secondary-800 peer-hover:peer-checked:border-primary-800",
                        "peer-active:border-secondary-800 peer-active:peer-checked:border-primary-800",
                        "peer-focus:border-secondary-800 peer-focus:peer-checked:border-primary-800",
                        "peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2",
                        "peer-focus-visible:border-gray-300 peer-focus-visible:peer-checked:border-primary-700",
                        "peer-focus-visible:ring-primary-800 peer-focus-visible:peer-checked:ring-primary-800",
                    )
                )
            }, "border-gray-300")

            labelWrapper {
                optionTextList.forEach {
                    div { +it }
                }
            }
        }
    }
}

/**
 * Rendert die Kundenportal-eigene Variante einer Button-Toggle-Komponente.
 *
 * @param id ID der Komponente
 * @param initialize Konfigurationsblock der Komponente
 */
fun <T> RenderContext.buttonToggleKupo(
    id: String? = null,
    initialize: ButtonToggleKupo<T>.() -> Unit
): Tag<HTMLDivElement> =
    with(ButtonToggleKupo<T>(id)) {
        initialize()
        render()
    }